// Unit 11 — Unicast, Multicast and Broadcast (Lesson + guided prediction on the diagram)
import { h } from '../../ui/h.js';
import { renderTopology } from '../../ui/topology.js';
import { observe, readConfirm } from '../../ui/widgets.js';
import { star } from './topologies.js';
import { ran, pinged, sameMac } from './helpers.js';

const layout = {
  width: 900, height: 360,
  nodes: {
    PC1: { x: 100, y: 70, type: 'pc', sub: '192.168.10.10' },
    PC2: { x: 100, y: 290, type: 'pc', sub: '192.168.10.11' },
    PC3: { x: 600, y: 310, type: 'pc', sub: '192.168.10.12' },
    PC4: { x: 780, y: 290, type: 'pc', sub: '192.168.10.13' },
    SW1: { x: 450, y: 150, type: 'switch', sub: 'VLAN 1' },
    R1: { x: 780, y: 70, type: 'router', sub: '192.168.10.1' },
  },
  zones: [{ x: 20, y: 20, w: 860, h: 330, label: 'ONE BROADCAST DOMAIN · VLAN 1' }],
};
const ALL = ['FastEthernet0/1', 'FastEthernet0/2', 'FastEthernet0/3', 'FastEthernet0/4', 'FastEthernet0/24'].map((p) => `SW1:${p}`);

/** Click the switch ports a frame will leave from; then reveal what really happens. */
function portPredict({ id, prompt, answer, explain }) {
  return {
    id, blocker: 'Commit your port prediction',
    // any committed prediction counts (it is compared with the answer afterwards)
    verify: (d) => Array.isArray(d?.sel) && d.sel.length > 0 && d.sel.every((k) => ALL.includes(k)),
    mount(el, rt, api) {
      const s = api.state.data;
      const sel = new Set(s.sel || []);
      const render = () => {
        el.textContent = '';
        el.append(h('div', { class: 'w-title' }, 'PREDICT ON THE DIAGRAM', api.state.done ? h('span', { class: 'ok' }, '✓ RECORDED') : null), h('p', {}, prompt));
        const result = api.state.done ? Object.fromEntries(ALL.map((k) => [k, answer.includes(k) ? (sel.has(k) ? 'good' : 'miss') : sel.has(k) ? 'wrong' : ''])) : null;
        el.append(renderTopology(layout, rt.net, {
          selectPorts: { selected: sel, result, onToggle: (k) => { if (api.state.done) return; sel.has(k) ? sel.delete(k) : sel.add(k); s.sel = [...sel]; render(); } },
        }));
        if (!api.state.done) {
          el.append(h('div', { class: 'row' }, h('span', { class: 'muted small' }, `${sel.size} port(s) selected — click the dashed circles on SW1’s side of each link.`),
            h('button', { class: 'btn small', 'data-testid': `pp-commit-${id}`, disabled: !sel.size || null, onclick: () => { rt.action('predict', { widget: id, ports: [...sel] }); api.done(); render(); } }, 'Send the frame')));
        } else {
          const right = ALL.every((k) => answer.includes(k) === sel.has(k));
          el.append(h('div', { class: `w-fb ${right ? 'good' : 'bad'}` }, right ? 'Exactly right. ' : 'Not quite — green = predicted correctly, amber = missed, red = the frame never went there. ', explain));
        }
      };
      render();
    },
  };
}

export default {
  id: 'u11', num: '11', title: 'Unicast, Multicast and Broadcast', type: 'Guided Activity', implemented: true,
  objective: 'Distinguish unicast, broadcast and multicast by their addresses, predict how a switch forwards each, and confirm it with ARP and MAC-table evidence.',
  network: () => {
    const n = star({ pcs: ['PC1', 'PC2', 'PC3', 'PC4'], ports: [1, 2, 3, 4], withRouter: true });
    n.devices.PC4.nic.mac = '005079666b03';
    return n;
  },
  layout, linkLights: true,
  stages: [
    {
      id: 'learn', phase: 'LEARN', title: 'One, all, or some', workspace: false,
      content: `
<table><tr><th>Type</th><th>Who receives it</th><th>IPv4 destination</th><th>MAC destination</th><th>Example</th></tr>
<tr><td><b>Unicast</b></td><td>One host</td><td>A host address</td><td>That host’s MAC</td><td>Web, SSH, file copy</td></tr>
<tr><td><b>Broadcast</b></td><td>Every host in the broadcast domain</td><td>255.255.255.255 or the subnet broadcast (e.g. 192.168.10.255)</td><td><code>ffff.ffff.ffff</code></td><td>ARP request, DHCP Discover</td></tr>
<tr><td><b>Multicast</b></td><td>Hosts that joined a group</td><td>224.0.0.0/4</td><td><code>01-00-5e-…</code> (low 23 bits of the group)</td><td>OSPF (224.0.0.5), mDNS (224.0.0.251), IPTV</td></tr></table>
<h3>What the switch does</h3>
<ul><li><b>Known unicast</b> → out of the one port in the MAC table.</li>
<li><b>Unknown unicast</b> → flooded (the switch has not learned it yet).</li>
<li><b>Broadcast</b> → flooded out of every port in the VLAN <i>except the one it arrived on</i>.</li>
<li><b>Multicast</b> → flooded like a broadcast, unless IGMP snooping limits it.</li></ul>
<p><b>Routers do not forward broadcasts.</b> A VLAN/subnet is a broadcast domain. IPv6 has no broadcast at all — it uses multicast.</p>`,
      widgets: [readConfirm({ id: 'u11-read' })],
      question: 'Why do very large VLANs perform badly even when nobody is transferring big files?',
    },
    {
      id: 'predict1', phase: 'PREDICT', title: 'Where does the ARP request go?', workspace: false,
      content: '<p>PC1 wants to ping PC3 (192.168.10.12) but has an empty ARP cache. It sends an ARP request to <code>ffff.ffff.ffff</code>.</p>',
      widgets: [portPredict({ id: 'u11-p1', prompt: 'Select every SW1 port the ARP request will be sent OUT of.', answer: ALL.filter((k) => k !== 'SW1:FastEthernet0/1'), explain: 'A broadcast is flooded out of every port in the VLAN except the ingress port (Fa0/1) — including towards R1.' })],
    },
    {
      id: 'predict2', phase: 'PREDICT', title: 'Where does the reply go?', workspace: false,
      content: '<p>PC3 answers with an ARP <b>reply</b> addressed to PC1’s MAC. SW1 learned PC1’s MAC on Fa0/1 when the request came in.</p>',
      widgets: [portPredict({ id: 'u11-p2', prompt: 'Select every SW1 port the ARP reply will be sent OUT of.', answer: ['SW1:FastEthernet0/1'], explain: 'The reply is a known unicast: SW1 forwards it out of Fa0/1 only.' })],
    },
    {
      id: 'verify', phase: 'VERIFY', title: 'Confirm it on the devices', devices: ['PC1', 'SW1'],
      content: `<ol><li>PC1: <code>arp -d</code> to empty the cache, then <code>arp -a</code>.</li><li>SW1: <code>clear mac address-table dynamic</code>.</li><li>PC1: <code>ping 192.168.10.12</code>, then <code>arp -a</code> again.</li><li>SW1: <code>show mac address-table</code>.</li></ol>`,
      evidence: [
        { id: 'flush', text: 'PC1: ARP cache emptied', match: ran('PC1', 'arp -d') },
        { id: 'clear', text: 'SW1: dynamic MAC entries cleared', match: ran('SW1', 'clear mac address-table dynamic') },
        { id: 'ping', text: 'PC1: ping to PC3 succeeds', match: pinged('PC1', '192.168.10.12', { success: true }) },
        { id: 'arp', text: 'PC1: ARP cache now holds PC3', match: ran('PC1', 'arp -a', /192\.168\.10\.12\s+00-50-79/) },
        { id: 'mac', text: 'SW1: MAC table shows who transmitted', match: (ev) => ran('SW1', ['show mac address-table', 'show mac address-table dynamic'])(ev) && /DYNAMIC/.test(ev.output) },
      ],
      widgets: [observe({
        id: 'u11-obs', title: 'ADDRESS EVIDENCE',
        fields: [
          { label: 'Destination MAC of an ARP request (any notation)', check: (v) => sameMac(v, 'ffffffffffff') || 'Which MAC address reaches every host?' },
          { label: 'MAC that PC1 maps the mDNS group 224.0.0.251 to (see arp -a)', check: (v) => sameMac(v, '01005e0000fb') || 'Find 224.0.0.251 in PC1’s arp -a output.' },
          { label: 'Destination MAC of PC3’s ARP reply', check: (v, rt) => sameMac(v, rt.net.devices.PC1.nic.mac) || 'The reply goes back to the host that asked.' },
          { label: 'Did SW1 learn PC2’s MAC during this test? (yes / no)', check: (v, rt) => (rt.net.devices.SW1.macTable.some((e) => e.mac === rt.net.devices.PC2.nic.mac) ? /^y/i.test(v) : /^n/i.test(v)) || 'Receiving a flooded broadcast is not transmitting. Check the table.' },
        ],
      })],
      question: 'PC2 and PC4 received PC1’s broadcast but do not appear in the MAC table. What does that confirm about how switches learn?',
    },
  ],
  review: () => '<p>Broadcasts are flooded to the whole VLAN, replies are unicast, and multicast groups map to <code>01-00-5e</code> MACs. The MAC table only shows hosts that <b>sent</b> something.</p>',
};
